import { useState, useEffect, useRef, useCallback } from 'react';
import { destinations } from '../data/data';
import './Hero.css';

// Sanitize URLs used in CSS background-image to prevent CSS injection
const safeBgUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return '';
    return `url("${u.href.replace(/"/g, '%22')}")`;
  } catch {
    return '';
  }
};

const DURATION = 3000;

export default function Hero({ onSearch }) {
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Search state
  const [searchData, setSearchData] = useState({
    destination: '', dates: '', travellers: '2 Adults', type: 'All Types'
  });
  const [activeField, setActiveField] = useState(null);

  const autoRef = useRef(null);
  const progressRef = useRef(null);
  const startRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0 });

  const goTo = useCallback((next) => {
    if (isAnimating || next === current) return;
    setIsAnimating(true);
    setExiting(current);
    clearTimeout(autoRef.current);
    clearInterval(progressRef.current);
    setProgress(0);

    setTimeout(() => {
      setCurrent(next);
      setExiting(null);
      setIsAnimating(false);
    }, 900);
  }, [current, isAnimating]);

  const nextSlide = useCallback(() => goTo((current + 1) % destinations.length), [current, goTo]);
  const prevSlide = useCallback(() => goTo((current - 1 + destinations.length) % destinations.length), [current, goTo]);

  // Progress bar
  useEffect(() => {
    startRef.current = Date.now();
    setProgress(0);
    clearInterval(progressRef.current);
    progressRef.current = setInterval(() => {
      const pct = Math.min(((Date.now() - startRef.current) / DURATION) * 100, 100);
      setProgress(pct);
    }, 40);
    clearTimeout(autoRef.current);
    autoRef.current = setTimeout(nextSlide, DURATION);
    return () => { clearInterval(progressRef.current); clearTimeout(autoRef.current); };
  }, [current, nextSlide]);

  // Keyboard — skip slide nav when user is typing in an input
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nextSlide, prevSlide]);

  // Mouse parallax
  const onMouseMove = (e) => {
    setMousePos({
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2,
    });
  };

  // Drag/swipe
  const onMouseDown = (e) => { dragRef.current = { dragging: true, startX: e.clientX }; };
  const onMouseUp = (e) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 60) dx < 0 ? nextSlide() : prevSlide();
  };
  const onTouchStart = (e) => { dragRef.current = { dragging: true, startX: e.touches[0].clientX }; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - dragRef.current.startX;
    if (Math.abs(dx) > 40) dx < 0 ? nextSlide() : prevSlide();
  };

  const handleSearch = () => {
    onSearch(searchData);
    setActiveField(null);
  };

  return (
    <section
      id="hero"
      className="hero"
      onMouseMove={onMouseMove}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Slides */}
      {destinations.map((d, i) => (
        <div
          key={d.id}
          className={`hero__slide ${i === current ? 'hero__slide--active' : ''} ${i === exiting ? 'hero__slide--exiting' : ''}`}
        >
          <div
            className="hero__bg"
            style={{
              backgroundImage: safeBgUrl(d.img),
              transform: i === current
                ? `scale(1.08) translate(${mousePos.x * -12}px, ${mousePos.y * -8}px)`
                : 'scale(1.08)',
            }}
          />
          <div className="hero__vignette" />
          <div className="hero__grain" />

          <div className="hero__content">
            <div className="hero__eyebrow">{d.country}</div>
            <h1 className="hero__name">{d.name}</h1>
            <p className="hero__desc">{d.desc}</p>
          </div>
        </div>
      ))}

      {/* ── SEARCH BAR (MakeMyTrip-style, embedded in hero) ── */}
      {/* <div className="hero__search-wrap">
        <div className="hero__search-tabs">
          {['Tours', 'Flights + Hotel', 'Custom Trip'].map((t) => (
            <button
              key={t}
              className={`hero__search-tab ${t === 'Tours' ? 'hero__search-tab--active' : ''}`}
              onClick={() => {}}
            >{t}</button>
          ))}
        </div>

        <div className="hero__search-bar">
          <div
            className={`hero__sf ${activeField === 'destination' ? 'hero__sf--active' : ''}`}
            onClick={() => setActiveField(activeField === 'destination' ? null : 'destination')}
          >
            <div className="hero__sf-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                <circle cx="12" cy="9" r="2.5"/>
              </svg>
            </div>
            <div className="hero__sf-content">
              <div className="hero__sf-label">Where to?</div>
              <input
                className="hero__sf-input"
                placeholder="Destination or tour"
                value={searchData.destination}
                onChange={e => setSearchData(v => ({ ...v, destination: e.target.value }))}
                onClick={e => e.stopPropagation()}
              />
            </div>
            {activeField === 'destination' && (
              <div className="hero__sf-dropdown">
                {['Santorini, Greece', 'Rajasthan, India', 'Maldives', 'Kyoto, Japan', 'Amalfi Coast, Italy', 'Patagonia'].map(dest => (
                  <div key={dest} className="hero__sf-option" onClick={() => { setSearchData(v => ({ ...v, destination: dest })); setActiveField(null); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
                    {dest}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hero__sf-divider" />

          <div
            className={`hero__sf ${activeField === 'dates' ? 'hero__sf--active' : ''}`}
            onClick={() => setActiveField(activeField === 'dates' ? null : 'dates')}
          >
            <div className="hero__sf-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
              </svg>
            </div>
            <div className="hero__sf-content">
              <div className="hero__sf-label">Travel Dates</div>
              <input
                className="hero__sf-input"
                placeholder="Check in – Check out"
                value={searchData.dates}
                onChange={e => setSearchData(v => ({ ...v, dates: e.target.value }))}
                onClick={e => e.stopPropagation()}
              />
            </div>
            {activeField === 'dates' && (
              <div className="hero__sf-dropdown">
                {['Next week', 'This month', 'Next month', 'In 3 months', 'Custom dates'].map(d => (
                  <div key={d} className="hero__sf-option" onClick={() => { setSearchData(v => ({ ...v, dates: d })); setActiveField(null); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                    {d}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hero__sf-divider" />

          <div
            className={`hero__sf ${activeField === 'travellers' ? 'hero__sf--active' : ''}`}
            onClick={() => setActiveField(activeField === 'travellers' ? null : 'travellers')}
          >
            <div className="hero__sf-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/>
              </svg>
            </div>
            <div className="hero__sf-content">
              <div className="hero__sf-label">Travellers</div>
              <div className="hero__sf-input hero__sf-val">{searchData.travellers}</div>
            </div>
            {activeField === 'travellers' && (
              <div className="hero__sf-dropdown">
                {['Solo', '2 Adults', '2 Adults, 1 Child', '2 Adults, 2 Children', 'Group (6+)'].map(t => (
                  <div key={t} className="hero__sf-option" onClick={() => { setSearchData(v => ({ ...v, travellers: t })); setActiveField(null); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="7" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>
                    {t}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hero__sf-divider" />

          <div
            className={`hero__sf ${activeField === 'type' ? 'hero__sf--active' : ''}`}
            onClick={() => setActiveField(activeField === 'type' ? null : 'type')}
          >
            <div className="hero__sf-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M7 12h10M10 18h4"/>
              </svg>
            </div>
            <div className="hero__sf-content">
              <div className="hero__sf-label">Tour Type</div>
              <div className="hero__sf-input hero__sf-val">{searchData.type}</div>
            </div>
            {activeField === 'type' && (
              <div className="hero__sf-dropdown">
                {['All Types', 'Luxury', 'Adventure', 'Cultural', 'Romantic', 'Family', 'Budget'].map(t => (
                  <div key={t} className="hero__sf-option" onClick={() => { setSearchData(v => ({ ...v, type: t })); setActiveField(null); }}>
                    {t}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button className="hero__search-btn" onClick={handleSearch}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            Search
          </button>
        </div>
      </div> */}

      {/* Dots */}
      <div className="hero__dots">
        {destinations.map((_, i) => (
          <button key={i} className={`hero__dot ${i === current ? 'hero__dot--active' : ''}`} onClick={() => goTo(i)} />
        ))}
      </div>


      {/* Progress bar */}
      <div className="hero__progress" style={{ width: `${progress}%` }} />

      {/* Click catcher for dropdowns */}
      {activeField && <div className="hero__backdrop" onClick={() => setActiveField(null)} />}
    </section>
  );
}
